import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './Checkout.scss';
import useCart from '../firebase files/hooks/useCart';
import { useCollection } from '../firebase files/hooks/useCollection';
import { useDocument } from '../firebase files/hooks/useDocument';
import Skeleton from "react-loading-skeleton";
import { FaCheckCircle, FaPrint } from 'react-icons/fa';

const Checkout = () => {
    const { cartItems } = useCart();
    const { documents: productDocuments, error: productErrors } =
        useCollection("products");

    const { document: currencyDocument, error: currencyError } = useDocument(
        "settings",
        "currency"
    );
    const [step, setStep] = useState(1);
    const [personalInfo, setPersonalInfo] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        flatNumber: '',
        streetLine1: '',
        streetLine2: '',
        landmark: '',
        city: '',
        state: '',
        pincode: ''
    });

    const [isLoading, setIsLoading] = useState(true);
    const [paymentMethod, setPaymentMethod] = useState('cashOnDelivery');
    const [billingInfo, setBillingInfo] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        flatNumber: '',
        streetLine1: '',
        streetLine2: '',
        landmark: '',
        city: '',
        state: '',
        pincode: ''
    });

    const [orderId, setOrderId] = useState(null);
    const receiptRef = useRef(null);

    useEffect(() => {
        if (currencyDocument && productDocuments) {
            setIsLoading(false);
        }
    }, [currencyDocument, productDocuments]);

    useEffect(() => {
        window.scrollTo(0, 0);
      }, [step]);



    const getProduct = (productId) => {
        return (
            productDocuments &&
            productDocuments.find((product) => product.id === productId)
        );
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setPersonalInfo(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleNext = () => {
        setStep(prevStep => Math.min(prevStep + 1, 4));
    };

    const handleBack = () => {
        setStep(prevStep => Math.max(prevStep - 1, 1));
    };

    const handlePaymentMethodChange = (method) => {
        setPaymentMethod(method);
    };

    const handleBillingInfoChange = (e) => {
        const { name, value } = e.target;
        setBillingInfo(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const fillBillingWithShipping = () => {
        setBillingInfo(personalInfo);
    };

    const calculateTotal = () => {
        return cartItems.reduce((total, item) => {
            const product = getProduct(item.productId);
            return total + (product.price * item.quantity);
        }, 0);
    };

    const generateOrderId = () => {
        return 'ORD-' + Math.random().toString(36).substr(2, 9).toUpperCase();
    };

    const handlePlaceOrder = () => {
        // Here you would typically send the order to your backend
        // For this example, we'll just generate a random order ID
        const newOrderId = generateOrderId();
        setOrderId(newOrderId);
        setStep(4);
    };

    const handlePrintReceipt = () => {
        const printContent = receiptRef.current.innerHTML;
        const originalContent = document.body.innerHTML;
        document.body.innerHTML = printContent;
        window.print();
        document.body.innerHTML = originalContent;
    };

    const renderStep = () => {
        switch (step) {
            case 1:
                return (
                    <div className="step-content">
                        <div className="personal-info">
                            <h2>Personal Information</h2>
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="firstName">Fullname</label>
                                    <input type="text" id="firstName" name="firstName" value={personalInfo.firstName} onChange={handleChange} />
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="email">Email</label>
                                    <input type="email" id="email" name="email" value={personalInfo.email} onChange={handleChange} />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="phone">Phone</label>
                                    <input type="tel" id="phone" name="phone" value={personalInfo.phone} onChange={handleChange} />
                                </div>
                            </div>
                            <h3>Shipping Address</h3>
                            <div className="form-group">
                                <label htmlFor="flatNumber">Flat/House Number</label>
                                <input type="text" id="flatNumber" name="flatNumber" value={personalInfo.flatNumber} onChange={handleChange} />
                            </div>
                            <div className="form-group">
                                <label htmlFor="streetLine1">Street Address Line 1</label>
                                <input type="text" id="streetLine1" name="streetLine1" value={personalInfo.streetLine1} onChange={handleChange} />
                            </div>
                            <div className="form-group">
                                <label htmlFor="streetLine2">Street Address Line 2</label>
                                <input type="text" id="streetLine2" name="streetLine2" value={personalInfo.streetLine2} onChange={handleChange} />
                            </div>
                            <div className="form-group">
                                <label htmlFor="landmark">Nearby Landmark</label>
                                <input type="text" id="landmark" name="landmark" value={personalInfo.landmark} onChange={handleChange} />
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="city">City</label>
                                    <input type="text" id="city" name="city" value={personalInfo.city} onChange={handleChange} />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="state">State</label>
                                    <input type="text" id="state" name="state" value={personalInfo.state} onChange={handleChange} />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="pincode">Pincode</label>
                                    <input type="text" id="pincode" name="pincode" value={personalInfo.pincode} onChange={handleChange} />
                                </div>
                            </div>
                        </div>
                        <div className="checkout-cart-summary">
                            <div className='checkout-cart-summary-header'>
                                <h3>Cart Summary</h3>
                                <Link to="/cart2" className="edit-cart-btn">Edit Cart</Link>
                            </div>
                            {
                                cartItems && cartItems.map((item, index) => {
                                    const product = getProduct(item.productId);
                                    return (
                                        <div key={index} className="checkout-cart-item">
                                            <img src={product.images && product.images[0]} alt={product.name} />
                                            <div className="checkout-cart-item-details">
                                                <h4>{product.name}</h4>
                                                <div>
                                                    <span>Price: {currencyDocument.symbol}{product.price}</span>
                                                    <span>Qty: {item.quantity}</span>
                                                </div>
                                                <p>Total: {currencyDocument.symbol}{product.price * item.quantity}</p>
                                            </div>
                                        </div>
                                    )
                                })
                            }
                        </div>
                    </div>
                );
            case 2:
                return (
                    <div className="step-content payment-step">
                        <h2>Payment Information</h2>
                        <div className="payment-tabs">
                            <button
                                className={`payment-tab ${paymentMethod === 'cashOnDelivery' ? 'active' : ''}`}
                                onClick={() => handlePaymentMethodChange('cashOnDelivery')}
                            >
                                Cash on Delivery
                            </button>
                            <button
                                className={`payment-tab ${paymentMethod === 'onlinePayment' ? 'active' : ''}`}
                                onClick={() => handlePaymentMethodChange('onlinePayment')}
                            >
                                Online Payment
                            </button>
                        </div>
                        {paymentMethod === 'cashOnDelivery' ? (
                            <div className="billing-info">
                                <div className="billing-info-header">
                                    <h2>Billing Information</h2>
                                    <button className="fill-shipping-btn" onClick={fillBillingWithShipping}>Same as Shipping Address</button>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="firstName">Fullname</label>
                                        <input type="text" id="firstName" name="firstName" value={personalInfo.firstName} onChange={handleChange} />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="email">Email</label>
                                        <input type="email" id="email" name="email" value={personalInfo.email} onChange={handleChange} />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="phone">Phone</label>
                                        <input type="tel" id="phone" name="phone" value={personalInfo.phone} onChange={handleChange} />
                                    </div>
                                </div>
                                <h3>Billing Address</h3>
                                <div className="form-group">
                                    <label htmlFor="flatNumber">Flat/House Number</label>
                                    <input type="text" id="flatNumber" name="flatNumber" value={personalInfo.flatNumber} onChange={handleChange} />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="streetLine1">Street Address Line 1</label>
                                    <input type="text" id="streetLine1" name="streetLine1" value={personalInfo.streetLine1} onChange={handleChange} />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="streetLine2">Street Address Line 2</label>
                                    <input type="text" id="streetLine2" name="streetLine2" value={personalInfo.streetLine2} onChange={handleChange} />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="landmark">Nearby Landmark</label>
                                    <input type="text" id="landmark" name="landmark" value={personalInfo.landmark} onChange={handleChange} />
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="city">City</label>
                                        <input type="text" id="city" name="city" value={personalInfo.city} onChange={handleChange} />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="state">State</label>
                                        <input type="text" id="state" name="state" value={personalInfo.state} onChange={handleChange} />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="pincode">Pincode</label>
                                        <input type="text" id="pincode" name="pincode" value={personalInfo.pincode} onChange={handleChange} />
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="online-payment-info">
                                <h3>Online Payment</h3>
                                <p>Coming soon! We're working on bringing you secure online payment options.</p>
                            </div>
                        )}
                    </div>
                );
            case 3:
                return (
                    <div className="step-content order-review">
                        <div className="order-details">
                            <h2>Order Review</h2>
                            <div className="address-section">
                                <h3>Shipping Address</h3>
                                <p>Ankur Yadav</p>
                                <p>C-101 , Tower 2</p>
                                <p>SeaLine Apartments</p>
                                <p>Gurugram, Haryana, 122001</p>
                                <p>91-9876543210</p>
                            </div>
                            <div className="address-section">
                                <h3>Billing Address</h3>
                                <p>Ankur Yadav</p>
                                <p>C-101 , Tower 2</p>
                                <p>SeaLine Apartments</p>
                                <p>Gurugram, Haryana, 122001</p>
                                <p>91-9876543210</p>
                            </div>
                            <div className="cart-items">
                                <h3>Order Items</h3>
                                {cartItems.map((item, index) => {
                                    const product = getProduct(item.productId);
                                    return (
                                        <div key={index} className="cart-item">
                                            <img src={product.images[0]} alt={product.name} />
                                            <div className="item-details">
                                                <h4>{product.name}</h4>
                                                <p>Quantity: {item.quantity}</p>
                                                <p>Price: {currencyDocument.symbol}{product.price * item.quantity}</p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="order-summary">
                            <h3>Order Summary</h3>
                            <div className="summary-row">
                                <span>Subtotal:</span>
                                <span>{currencyDocument.symbol}{calculateTotal()}</span>
                            </div>
                            <div className="summary-row">
                                <span>Shipping:</span>
                                <span>Free</span>
                            </div>
                            <div className="summary-row total">
                                <span>Total:</span>
                                <span>{currencyDocument.symbol}{calculateTotal()}</span>
                            </div>
                            <button className="place-order-btn" onClick={handlePlaceOrder}>Place Order</button>
                        </div>
                    </div>
                );
            case 4:
                return (
                    <div className="step-content order-confirmation" ref={receiptRef}>
                        <div className="confirmation-header">
                            <FaCheckCircle className="check-icon" />
                            <h2>Order Placed Successfully!</h2>
                            <p>Order ID: 1XAY234ZN</p>
                        </div>
                        <div className="order-details">
                            <h3>Order Details</h3>
                            <div className="details-flex">
                                <div className="detail-item">
                                    <h4>Shipping Address</h4>
                                    <p>C-101 , Tower 2</p>
                                    <p>SeaLine Apartments</p>
                                    <p>Gurugram, Haryana, 122001</p>
                                    <p>91-9876543210</p>
                                </div>
                                <div className="detail-item">
                                    <h4>Payment Method</h4>
                                    <p>{paymentMethod === 'cashOnDelivery' ? 'Cash on Delivery' : 'Online Payment'}</p>
                                </div>
                                <div className="detail-item">
                                    <h4>Order Summary</h4>
                                    <p>Total Items: {cartItems.length}</p>
                                    <p>Total Amount: {currencyDocument.symbol}{calculateTotal()}</p>
                                </div>
                                <div className="detail-item">
                                    <h4>Estimated Delivery</h4>
                                    <p>{new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toDateString()}</p>
                                </div>
                            </div>
                        </div>
                        <div className="confirmation-actions">
                            <Link to="/" className="continue-shopping-btn">Continue Shopping</Link>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    if (isLoading) {
        return (
            <div style={{ height: "100vh", padding: "10px" }}>
                <Skeleton count={5} height={100} className="mb-4" />
            </div>
        );
    }

    return (
        <div className="checkout-container">
            <div className="new-progress-bar">
                <div className='progress' style={{ width: `${(step - 1) * 33.33}%` }}></div>
                <div className={`step-indicator ${step >= 1 ? 'active-step' : ''}`}>
                    <span className="step-number">1</span>
                    <span className="step-name">Personal Info</span>
                </div>
                <div className={`step-indicator ${step >= 2 ? 'active-step' : ''}`}>
                    <span className="step-number">2</span>
                    <span className="step-name">Payment</span>
                </div>
                <div className={`step-indicator ${step >= 3 ? 'active-step' : ''}`}>
                    <span className="step-number">3</span>
                    <span className="step-name">Review</span>
                </div>
                <div className={`step-indicator ${step >= 4 ? 'active-step' : ''}`}>
                    <span className="step-number">4</span>
                    <span className="step-name">Confirmation</span>
                </div>
            </div>
            {renderStep()}
            <div className="button-container">
                <button className="back-btn" onClick={handleBack} disabled={step === 1}>Back</button>
                <button className="next-btn" onClick={handleNext} disabled={step === 4}>Next</button>
            </div>
        </div>
    );
};

export default Checkout;
